import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { getSongPartUrl } from '../../services/songService';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

/**
 * Minimalist modal component for previewing song part files at maximum size
 */
const SongPartPreview = ({ show, onHide, song }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [showComment, setShowComment] = useState(true); // Default to showing comment
  const [isTroublesomeBrowser, setIsTroublesomeBrowser] = useState(false);
  const iframeRef = useRef(null);

  // Detect troublesome browsers (Chrome/Brave on Android)
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = userAgent.indexOf('android') > -1;
    const isChrome = userAgent.indexOf('chrome') > -1;
    const isBrave = userAgent.indexOf('brave') > -1;
    
    // Set flag for Chrome or Brave on Android
    setIsTroublesomeBrowser(isAndroid && (isChrome || isBrave));
  }, []);

  // Get the URL for the part file
  const partUrl = song ? getSongPartUrl(song.id) : '';

  // Fetch the PDF directly to eliminate CORS/Content-Type issues
  const fetchPdfContent = async () => {
    if (!song || !partUrl) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Skip PDF fetching for troublesome browsers as we'll show alternatives
      if (isTroublesomeBrowser && song.part_file_type === 'pdf') {
        setLoading(false);
        return;
      }
      
      // Fetch the PDF using axios to handle binary data
      const response = await axios.get(partUrl, {
        responseType: 'arraybuffer',
        withCredentials: true,
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      // Convert array buffer to base64
      const base64 = btoa(
        new Uint8Array(response.data)
          .reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      
      // Create data URL
      const dataUrl = `data:application/pdf;base64,${base64}`;
      setPdfData(dataUrl);
      setLoading(false);
    } catch (err) {
      setError(`Napaka pri nalaganju PDF-ja: ${err.message}`);
      setLoading(false);
    }
  };

  // Determine if the file is viewable in the browser
  const isViewable = () => {
    if (!song || !song.part_file_type) {
      return false;
    }
    
    // PDF files can be displayed in iframe except for troublesome browsers
    if (song.part_file_type === 'pdf') {
      return !isTroublesomeBrowser;
    }
    
    // Image files can be displayed with an img tag
    const viewableImageTypes = ['jpg', 'jpeg', 'png', 'gif'];
    return viewableImageTypes.includes(song.part_file_type.toLowerCase());
  };

  // Render the appropriate preview based on file type
  const renderPreview = () => {
    if (!song || !song.has_part_file) {
      return null;
    }
    
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center w-100 h-100">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Nalaganje...</span>
          </Spinner>
        </div>
      );
    }

    if (error) {
      return (
        <div className="d-flex justify-content-center align-items-center w-100 h-100">
          <div className="text-center">
            <p className="text-danger">{error}</p>
            <Button variant="secondary" onClick={onHide}>
              Zapri
            </Button>
          </div>
        </div>
      );
    }

    const isPdf = song.part_file_type === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(song.part_file_type?.toLowerCase());

    // Special handling for troublesome browsers with PDFs
    if (isPdf && isTroublesomeBrowser) {
      return (
        <div className="d-flex flex-column justify-content-center align-items-center w-100 h-100 bg-light">
          <div className="text-center p-4">
            <h5 className="mb-3">{song.title}</h5>
            <p className="text-muted mb-4">PDF preview is not available in this browser.</p>
            <Button 
              variant="primary" 
              href={partUrl} 
              target="_blank"
              className="d-flex align-items-center justify-content-center mx-auto"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
              Open PDF
            </Button>
          </div>
        </div>
      );
    }

    if (isPdf) {
      // Use the data URL if available, otherwise fall back to direct URL
      const pdfSource = pdfData || partUrl;
      
      return (
        <div className="w-100 h-100">
          <object
            ref={iframeRef}
            data={pdfSource}
            type="application/pdf"
            width="100%"
            height="100%"
            className="border-0"
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <div className="d-flex justify-content-center align-items-center w-100 h-100">
              <Button variant="secondary" onClick={onHide}>
                Zapri
              </Button>
            </div>
          </object>
        </div>
      );
    } else if (isImage) {
      return (
        <div 
          className="d-flex justify-content-center align-items-center w-100 h-100"
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <img 
            src={partUrl}
            alt={`Part for ${song.title}`}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%',
              objectFit: 'contain'
            }}
          />
        </div>
      );
    } else {
      return (
        <div className="d-flex justify-content-center align-items-center w-100 h-100">
          <Button variant="secondary" onClick={onHide}>
            Zapri
          </Button>
        </div>
      );
    }
  };

  // Add styling to fix fullscreen issues, especially on mobile
  useEffect(() => {
    if (show) {
      // Fix for fullscreen issues
      document.body.classList.add('modal-open-no-scroll');
      
      // Fix modal styling for fullscreen
      const modalElements = document.querySelectorAll('.modal, .modal-dialog, .modal-content, .modal-body');
      modalElements.forEach(el => {
        el.style.padding = '0';
        el.style.margin = '0';
        el.style.maxWidth = '100%';
        el.style.width = '100%';
        el.style.height = '100%';
      });
    } else {
      // Clean up
      document.body.classList.remove('modal-open-no-scroll');
    }
    
    return () => {
      document.body.classList.remove('modal-open-no-scroll');
    };
  }, [show]);

  // Fetch content when modal opens
  useEffect(() => {
    if (show && song && song.part_file_type === 'pdf') {
      fetchPdfContent();
    } else if (show && song) {
      // For non-PDF files, just set loading to false
      setLoading(false);
    }
  }, [show, song, partUrl, isTroublesomeBrowser]);

  // Comment component with toggle functionality
  const CommentDisplay = () => {
    if (!song || !song.comment) return null;
    
    return (
      <div 
        className="position-absolute top-0 start-0 p-3"
        style={{
          maxWidth: '80%',
          marginTop: '10px',
          marginLeft: '10px',
          transition: 'all 0.3s ease',
          zIndex: 1050 // Higher z-index to ensure it appears above content
        }}
      >
        {showComment ? (
          <div className="bg-light bg-opacity-90 rounded p-3 shadow-sm border border-secondary">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">
                <FontAwesomeIcon icon={faComment} className="me-2" />
                Notes for {song.title}
              </h6>
              <Button 
                variant="link" 
                className="p-0 text-muted" 
                onClick={() => setShowComment(false)}
                title="Hide comment"
              >
                ✕
              </Button>
            </div>
            <p className="mb-0 comment-text" style={{ whiteSpace: 'pre-wrap' }}>{song.comment}</p>
          </div>
        ) : (
          <Button 
            variant="light" 
            size="sm" 
            className="opacity-75 shadow-sm"
            onClick={() => setShowComment(true)}
            title="Show comment"
          >
            <FontAwesomeIcon icon={faComment} className="me-1" />
            Show Notes
          </Button>
        )}
      </div>
    );
  };

  // Add custom styles to fix modal display issues
  const customStyles = `
    .modal-open-no-scroll {
      overflow: hidden !important;
    }
    .full-screen-modal .modal-dialog {
      max-width: none;
      margin: 0;
      padding: 0;
      height: 100vh;
    }
    .full-screen-modal .modal-content {
      height: 100vh;
      border: none;
      border-radius: 0;
    }
    .full-screen-modal .modal-body {
      padding: 0;
      overflow: hidden;
      position: relative;
    }
  `;

  return (
    <>
      <style>{customStyles}</style>
      <Modal 
        show={show} 
        onHide={onHide}
        size="xl"
        centered
        fullscreen={true}
        dialogClassName="m-0 mw-100"
        contentClassName="h-100"
        className="full-screen-modal"
      >
        <Modal.Body className="p-0 h-100 position-relative">
          {/* Close button */}
          <div className="position-absolute top-0 end-0 p-2" style={{ zIndex: 1100 }}>
            <Button 
              variant="light" 
              size="sm" 
              onClick={onHide}
              className="rounded-circle opacity-75 shadow-sm"
            >
              ✕
            </Button>
          </div>

          {/* Comment display */}
          <CommentDisplay />

          {/* Preview content */}
          <div className="w-100 h-100 position-absolute top-0 start-0">
            {song && song.has_part_file ? (
              isViewable() ? (
                renderPreview()
              ) : (
                // For troublesome browsers or unsupported file types
                <div className="d-flex justify-content-center align-items-center w-100 h-100">
                  {isTroublesomeBrowser && song.part_file_type === 'pdf' ? (
                    renderPreview() // This will render the "Open PDF" button for troublesome browsers
                  ) : (
                    <Button variant="secondary" onClick={onHide}>
                      Zapri
                    </Button>
                  )}
                </div>
              )
            ) : (
              <div className="d-flex justify-content-center align-items-center w-100 h-100">
                <Button variant="secondary" onClick={onHide}>
                  Zapri
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SongPartPreview;